/**
 * Input controlado para montos CLP con formato chileno en vivo.
 * Sprint 17 S17-Z13.
 *
 * Mientras el usuario tipea dígitos, el input muestra "$1.234.567"
 * con separadores chilenos (punto miles, sin decimales). Solo dígitos
 * son aceptados; cualquier otro carácter se filtra silenciosamente.
 *
 * El cursor se preserva en la posición relativa correcta tras
 * inserción/borrado — cuenta dígitos antes del cursor en el valor
 * viejo y reposiciona en el nuevo formatted al mismo offset de dígitos.
 *
 * Reemplaza `<input type="number">` plano (que no permite formato
 * mientras se escribe).
 *
 * El componente recibe `value: number | null` y emite `onChange(v: number | null)`,
 * conviene cambiar el state del caller a `useState<number | null>(null)`.
 */
import { useEffect, useRef, type ChangeEvent } from 'react';
import { formatCLP } from '../../lib/format';

export interface InputMontoCLPProps {
  /** Valor numérico actual. null cuando el input está vacío. */
  value: number | null;
  /** Callback con el nuevo valor numérico. null si el input quedó vacío. */
  onChange: (v: number | null) => void;
  /** Marca el input como requerido (HTML5 form validation). */
  required?: boolean;
  /** Placeholder cuando vacío. Default: "$0". */
  placeholder?: string;
  /** Bound máximo opcional. Si el usuario tipea más, se clampea. */
  max?: number;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
  autoFocus?: boolean;
  /** Aria-label para accesibilidad si no hay label asociada. */
  'aria-label'?: string;
}

export function InputMontoCLP({
  value,
  onChange,
  required,
  placeholder = '$0',
  max,
  disabled,
  className,
  id,
  name,
  autoFocus,
  'aria-label': ariaLabel,
}: InputMontoCLPProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  // Posición target del cursor tras el próximo render. null = no mover.
  const cursorTarget = useRef<number | null>(null);

  const displayed = value === null ? '' : formatCLP(value);

  useEffect(() => {
    if (cursorTarget.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(
        cursorTarget.current,
        cursorTarget.current,
      );
      cursorTarget.current = null;
    }
  });

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const input = e.target;
    const oldValue = input.value;
    const cursorPos = input.selectionStart ?? oldValue.length;
    // Solo dígitos del input.
    const digits = oldValue.replace(/\D/g, '');
    // Cuántos dígitos hay estrictamente antes del cursor en oldValue.
    const digitsBeforeCursor = oldValue
      .slice(0, cursorPos)
      .replace(/\D/g, '').length;

    if (digits === '') {
      onChange(null);
      cursorTarget.current = 0;
      return;
    }

    let num = Number(digits);
    if (max !== undefined && num > max) num = max;
    // Calcular dónde queda el cursor en el formatted nuevo: avanzar
    // hasta haber pasado `digitsBeforeCursor` dígitos.
    const newFormatted = formatCLP(num);
    let pos = 0;
    let count = 0;
    while (pos < newFormatted.length && count < digitsBeforeCursor) {
      if (/\d/.test(newFormatted[pos]!)) count++;
      pos++;
    }
    cursorTarget.current = pos;
    onChange(num);
  }

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      value={displayed}
      onChange={handleChange}
      required={required}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      id={id}
      name={name}
      autoFocus={autoFocus}
      aria-label={ariaLabel}
    />
  );
}
